import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type TooltipProps = {
  label: string;
  description?: string;
  children: React.ReactElement;
  side?: "top" | "bottom";
  className?: string;
};

type TooltipPosition = {
  left: number;
  top: number;
  transform: string;
  visibility: "hidden" | "visible";
};

const TOOLTIP_OPEN_EVENT = "causit:tooltip-open";
const VIEWPORT_PADDING = 8;
const GAP = 8;

export const Tooltip: React.FC<TooltipProps> = ({
  label,
  description,
  children,
  side = "bottom",
  className = "",
}) => {
  const id = useId();
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const tooltipRef = useRef<HTMLSpanElement | null>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<TooltipPosition>({
    left: 0,
    top: 0,
    transform: "translateX(-50%)",
    visibility: "hidden",
  });

  const close = () => setOpen(false);
  const show = () => {
    window.dispatchEvent(new CustomEvent(TOOLTIP_OPEN_EVENT, { detail: { id } }));
    setOpen(true);
  };
  const closeWhenLeaving = (event: React.MouseEvent<HTMLSpanElement> | React.PointerEvent<HTMLSpanElement>) => {
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) return;
    close();
  };

  const trigger = React.cloneElement(children, {
    "aria-describedby": id,
  });

  useEffect(() => {
    const onOpen = (event: Event) => {
      const detail = (event as CustomEvent<{ id?: string }>).detail;
      if (detail?.id !== id) setOpen(false);
    };
    window.addEventListener(TOOLTIP_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(TOOLTIP_OPEN_EVENT, onOpen);
  }, [id]);

  useLayoutEffect(() => {
    if (!open) return;

    const update = () => {
      const triggerNode = triggerRef.current;
      const tooltipNode = tooltipRef.current;
      if (!triggerNode || !tooltipNode) return;

      const triggerRect = triggerNode.getBoundingClientRect();
      const tooltipRect = tooltipNode.getBoundingClientRect();
      const wantedLeft = triggerRect.left + triggerRect.width / 2;
      const minLeft = VIEWPORT_PADDING + tooltipRect.width / 2;
      const maxLeft = window.innerWidth - VIEWPORT_PADDING - tooltipRect.width / 2;
      const left = Math.min(Math.max(wantedLeft, minLeft), maxLeft);
      const fitsBottom = triggerRect.bottom + GAP + tooltipRect.height <= window.innerHeight - VIEWPORT_PADDING;
      const fitsTop = triggerRect.top - GAP - tooltipRect.height >= VIEWPORT_PADDING;
      const actualSide = side === "top"
        ? fitsTop || !fitsBottom ? "top" : "bottom"
        : fitsBottom || !fitsTop ? "bottom" : "top";
      const top = actualSide === "top"
        ? Math.max(VIEWPORT_PADDING, triggerRect.top - GAP - tooltipRect.height)
        : Math.min(window.innerHeight - VIEWPORT_PADDING - tooltipRect.height, triggerRect.bottom + GAP);

      setPosition({
        left,
        top,
        transform: "translateX(-50%)",
        visibility: "visible",
      });
    };

    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, side]);

  return (
    <span
      ref={triggerRef}
      className={`inline-flex ${className}`}
      onPointerEnter={show}
      onPointerLeave={closeWhenLeaving}
      onMouseOver={show}
      onMouseOut={closeWhenLeaving}
      onFocus={show}
      onBlur={close}
      onClick={show}
    >
      {trigger}
      {open && typeof document !== "undefined" && createPortal(
        <span
          ref={tooltipRef}
          id={id}
          role="tooltip"
          className="pointer-events-none fixed z-[9999] grid w-max max-w-[220px] rounded-md border px-2.5 py-2 text-center text-[11px] leading-[15px] animate-tooltip-in"
          style={{
            ...position,
            backgroundColor: "var(--bg-modal)",
            borderColor: "var(--color-border)",
            color: "var(--color-text-primary)",
            boxShadow: "0 10px 28px rgba(0, 0, 0, 0.34)",
            opacity: 1,
          }}
        >
          <strong className="font-semibold">{label}</strong>
          {description && (
            <small
              className="mt-0.5 block"
              style={{ color: "var(--color-text-secondary)" }}
            >
              {description}
            </small>
          )}
        </span>,
        document.body,
      )}
    </span>
  );
};
